import { createPortal } from 'react-dom'

import { Dialog, Layout, Text } from '@loadsmart/miranda-react'

import { getDelivery, getPickup } from 'core/domain/Stop'
import { CardInfoItem } from 'core/ui/components/card/CardInfoItem'
import { formatCurrency } from 'core/utils/currency'
import { formatToLocaleDate } from 'core/utils/date'

import { type TenderOffer } from '../domain/TenderOffer'

export type AcceptTenderDialogProps = {
  tender: TenderOffer
  loading?: boolean
  onCancel: () => void
  onConfirm: () => void
  open?: boolean
}

export function AcceptTenderDialog({
  tender,
  loading,
  onCancel,
  onConfirm,
  open = false,
}: AcceptTenderDialogProps) {
  function handleClick(event: React.MouseEvent) {}

  function handleConfirm() {
    if (!loading) {
      onConfirm()
    }
  }

  function handleCancel() {
    if (!loading) {
      onCancel()
    }
  }

  const pickup = getPickup(tender.stops)
  const delivery = getDelivery(tender.stops)

  if (!pickup || !delivery) {
    return null
  }

  return createPortal(
    <Dialog
      data-testid="dialog-tender-accept"
      open={open}
      onClose={handleCancel}
      onClick={handleClick}
    >
      <Dialog.Header>
        Accept tender
        <Dialog.Close onClick={handleCancel} disabled={loading} />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          <Text>Please confirm your tender acceptance to proceed</Text>
          <Layout.Stack gap="spacing-0-5">
            <Text variant="body-sm">Pickup</Text>
            <Text variant="body-sm">{pickup.address}</Text>
            <Text variant="body-md-bold">
              {pickup.city}, {pickup.state} {pickup.zipCode}, {pickup.country || 'USA'}
            </Text>
            {pickup.date && (
              <Text variant="body-sm">
                {formatToLocaleDate(new Date(pickup.date), 'EEE, MMM d')}
              </Text>
            )}
          </Layout.Stack>
          <Layout.Stack gap="spacing-0-5">
            <Text variant="body-sm">Delivery</Text>
            <Text variant="body-sm">{delivery.address}</Text>
            <Text variant="body-md-bold">
              {delivery.city}, {delivery.state} {delivery.zipCode}, {delivery.country || 'USA'}
            </Text>
            {delivery.date && (
              <Text variant="body-sm">
                {formatToLocaleDate(new Date(delivery.date), 'EEE, MMM d')}
              </Text>
            )}
          </Layout.Stack>
          <Layout.Group style={{ textAlign: 'left' }}>
            <CardInfoItem title="Mode" value={tender.mode} style={{ flexGrow: 1 }} />
            <CardInfoItem
              title="Equipment"
              value={tender.equipmentTypeLabel || tender.equipmentType}
              style={{ flexGrow: 1 }}
            />
          </Layout.Group>
          <Layout.Box backgroundColor="color-background-tertiary" padding="spacing-3">
            <Layout.Group justify="flex-end">
              <CardInfoItem
                title="Distance"
                value={`${tender.mileage} miles`}
                style={{ flexGrow: 1 }}
              />
              <CardInfoItem title="Rate" value={formatCurrency(tender.flatRate)} />
            </Layout.Group>
          </Layout.Box>
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={handleCancel} loading={loading}>
          Cancel
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={handleConfirm} loading={loading}>
          Accept tender
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>,
    document.body
  )
}
